import { useState, useEffect, useCallback, useMemo } from 'react';
import debounce from 'lodash/debounce';
import { FaSearch, FaPrint, FaEye } from 'react-icons/fa';
import api from '../../services/api';
import {
  moeda,
  formataDataCompleta,
  formataDataSemHora,
} from '../../components/funcoes';
import '../style.css';
import DetalhesPedido from '../../components/DetalhesPedido';
import Modal from '../../components/Modal';
import { InputGroup, Pagination, Table } from 'rsuite';
import * as S from './styles';
import { useAuth } from '../../hooks/auth';
import { CustomInput } from '../../components/CustomInput';
import { useCustomTheme } from '../../hooks/useCustomTheme';
import { CustomHeaderCell } from '../../components/CustomHeaderCell';
import { CustomCell } from '../../components/CustomCell';
import { useConfig } from '../../hooks/config';
import Totalizador from './Totalizador';
import DuvidasButton from '../../components/DuvidasButton';
import { toastError } from '../../toasts';

const { Column } = Table;

const formaPagMap = {
  1: 'alelo',
  2: 'dinheiro',
  3: 'elo',
  4: 'visa',
  5: 'master',
  6: 'maestro',
  7: 'sodexo',
  8: 'ticket',
  9: 'vr',
  10: 'vale',
  11: 'cartao',
  12: 'transferencia',
  13: 'valeGas',
  14: 'hospedagem',
  15: 'prazo',
  16: 'debito',
  17: 'credito',
  99: 'pix',
};

const totaisIniciais = Object.fromEntries(
  Object.values(formaPagMap).map((key) => [key, 0]),
);

const deliveryTypeLabels = {
  LOCAL: 'No local',
  EN: 'Entrega',
  AG: 'Entrega',
  AGRE: 'Agendamento',
  RE: 'Retirada',
};

const allStatuses = [
  'PENDENTE',
  'ANDAMENTO',
  'ENTREGA',
  'CONCLUIDO',
  'CANCELADO',
];

export default function ConsultarPedidos() {
  const { id_empresa } = useAuth();
  const [buscar, setBuscar] = useState('');
  const [loading, setLoading] = useState(false);
  const [pedidos, setPedidos] = useState([]);
  const [pag, setPag] = useState(1);
  const [limite, setLimite] = useState(10);
  const [totalizadorBruto, setTotalizadorBruto] = useState(0);
  const [totalizadorLiquido, setTotalizadorLiquido] = useState(0);
  const [totalizadorTaxa, setTotalizadorTaxa] = useState(0);
  const [totalizadorTaxaAdm, setTotalizadorTaxaAdm] = useState(0);
  const [selectedStatuses, setSelectedStatuses] = useState([
    'PENDENTE',
    'ANDAMENTO',
    'ENTREGA',
    'CONCLUIDO',
  ]);
  const [listarTodos, setListarTodos] = useState(false);
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [horaInicio, setHoraInicio] = useState('00:00');
  const [horaFim, setHoraFim] = useState('23:59');
  const [qtdPedidos, setQtdPedidos] = useState(0);
  const [open, setOpen] = useState(false);
  const [entregadores, setEntregadores] = useState([]);
  const [pedidoSelecionado, setPedidoSelecionado] = useState({});
  const [formasPagamento, setFormasPagamento] = useState(totaisIniciais);
  const { theme } = useCustomTheme();
  const { config } = useConfig();

  const toggleStatus = (status) => {
    setListarTodos(false);
    setSelectedStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status],
    );
  };

  const handleListarTodos = () => {
    setListarTodos(true);
    setSelectedStatuses([]);
  };

  const atualizar = useCallback(async () => {
    await api.get(`entregador/listar/${id_empresa}`).then((res) => {
      setEntregadores(res.data);
    });
    setLoading(false);
  }, [id_empresa]);

const consultar = useCallback(async () => {
    const semFiltroDeStatus = selectedStatuses.length === 0;

    if (!listarTodos && semFiltroDeStatus && !buscar) {
      setPedidos([]);
      setQtdPedidos(0);
      setFormasPagamento(totaisIniciais);
      setTotalizadorBruto(0);
      setTotalizadorLiquido(0);
      setTotalizadorTaxa(0);
      setTotalizadorTaxaAdm(0);
      return;
    }

    setLoading(true);
    setPedidos([]);

    let dtInicio = dataInicio;
    let dtFim = dataFim;

    if (!dtInicio || !dtFim) {
      const hoje = new Date();
      const primeiroDia = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
      const ultimoDia = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);

      const formatarData = (d) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

      dtInicio = formatarData(primeiroDia);
      dtFim = formatarData(ultimoDia);

      setDataInicio(dtInicio);
      setDataFim(dtFim);
    }

    const payload = {
      IdEmpresa: id_empresa,
      DataInicio: dtInicio,
      DataFim: dtFim,
      HoraInicio: horaInicio || undefined,
      HoraFim: horaFim || undefined,
      Status: listarTodos || semFiltroDeStatus ? undefined : selectedStatuses,
      Busca: buscar || undefined,
      Limite: limite,
      Pagina: (pag - 1) * limite,
    };

    try {
      const res = await api.post('pedido/consultarPedidos', payload, { timeout: 10000 });

      const {
        count,
        data,
        totalBruto,
        totalLiquido,
        totalTaxa,
        totalTaxaAdm,
        totalPorFormaPagamento,
      } = res.data;

      setFormasPagamento(totalPorFormaPagamento);
      setTotalizadorBruto(totalBruto);
      setTotalizadorLiquido(totalLiquido);
      setTotalizadorTaxa(totalTaxa);
      setTotalizadorTaxaAdm(totalTaxaAdm);
      setPedidos(data ?? []);
      setQtdPedidos(count);

    } catch (error) {
      toastError(error.message ?? 'Erro ao consultar pedidos');
    } finally {
      setLoading(false);
    }
  }, [
    id_empresa,
    dataInicio,
    dataFim,
    horaInicio,
    horaFim,
    selectedStatuses,
    listarTodos,
    buscar,
    limite,
    pag,
  ]);

  const abrirDados = (pedido) => {
    setPedidoSelecionado(pedido);
    setOpen(true);
  };

  const imprimirPedido = (pedido) => {
    window.open(
      '/g_imprimir_pedido/' + pedido.Id,
      'minhaJanela',
      'height=800,width=800',
    );
  };

  const handleChangeLimit = (dataKey) => {
    setPag(1);
    setLimite(dataKey);
  };

  const debounceSearch = useMemo(
    () =>
      debounce((valor) => {
        setBuscar(valor);
        setPag(1);
      }, 500),
    [],
  );

  useEffect(() => {
    return () => debounceSearch.cancel();
  }, [debounceSearch]);

  useEffect(() => {
    consultar();
  }, [consultar, limite, pag]);

  useEffect(() => {
    atualizar();
  }, [atualizar]);

  const colStyle = {
    background: theme.colors.backgroundSecondary,
    color: theme.colors.textSecondary,
  };

  return (
    <S.PageWrapper>
      <S.PageHeader>
        <S.PageTitle>Consultar Pedidos</S.PageTitle>
        <S.SearchWrapper>
          <InputGroup inside style={{ border: 'none' }}>
            <CustomInput
              onChange={debounceSearch}
              placeholder="Buscar pedido..."
            />
            <InputGroup.Addon>
              <S.SearchIcon size={14} $spinning={loading} />
            </InputGroup.Addon>
          </InputGroup>
        </S.SearchWrapper>
        <DuvidasButton tela="consultar-pedidos" />
      </S.PageHeader>
      <S.FiltersBar>
        <S.FilterGroup>
          <S.FilterLabel>Data Inicial</S.FilterLabel>
          <S.DateTimeRow>
            <CustomInput
              type="date"
              value={dataInicio}
              onChange={setDataInicio}
            />
            <S.TimeInput
              type="time"
              value={horaInicio}
              onChange={(e) => setHoraInicio(e.target.value)}
              placeholder="00:00"
            />
          </S.DateTimeRow>
        </S.FilterGroup>

        <S.FilterGroup>
          <S.FilterLabel>Data Final</S.FilterLabel>
          <S.DateTimeRow>
            <CustomInput type="date" value={dataFim} onChange={setDataFim} />
            <S.TimeInput
              type="time"
              value={horaFim}
              onChange={(e) => setHoraFim(e.target.value)}
              placeholder="23:59"
            />
          </S.DateTimeRow>
        </S.FilterGroup>

        <S.FilterGroup $wide>
          <S.FilterLabel>
            Status{' '}
            <span
              style={{
                fontWeight: 400,
                textTransform: 'none',
                letterSpacing: 0,
              }}
            >
              - clique para filtrar
            </span>
          </S.FilterLabel>
          <S.StatusGroup>
            <S.StatusChip
              $status="TODOS"
              $active={listarTodos}
              onClick={handleListarTodos}
              title="Listar pedidos de todos os status"
            >
              Listar Todos
            </S.StatusChip>
            {allStatuses.map((s) => (
              <S.StatusChip
                key={s}
                $status={s}
                $active={selectedStatuses.includes(s)}
                onClick={() => toggleStatus(s)}
                title={
                  s === 'CANCELADO' ? 'Clique para incluir cancelados' : ''
                }
              >
                {s}
              </S.StatusChip>
            ))}
          </S.StatusGroup>
        </S.FilterGroup>

        <S.SearchBtn onClick={() => consultar()}>
          <FaSearch size={13} />
          Buscar
        </S.SearchBtn>
      </S.FiltersBar>

      <Totalizador
        quantidade={qtdPedidos}
        totalPagamentos={formasPagamento}
        totalizadorBruto={totalizadorBruto}
        totalizadorLiquido={totalizadorLiquido}
        totalizadorTaxas={totalizadorTaxa}
        totalizadorTaxaAdm={totalizadorTaxaAdm}
      />

      <S.TableWrapper>
        <Table
          height={window.innerHeight - 420}
          data={pedidos}
          loading={loading}
          style={{
            borderRadius: 10,
            overflow: 'hidden',
            backgroundColor: theme.colors.backgroundSecondary,
            color: theme.colors.textSecondary,
          }}
          locale={{
            emptyMessage: 'Nenhum pedido encontrado',
            loading: 'Carregando...',
          }}
        >
          <Column style={colStyle} width={80} fixed>
            <CustomHeaderCell>Código</CustomHeaderCell>
            <CustomCell dataKey="Codigo" />
          </Column>

          <Column style={colStyle} width={90}>
            <CustomHeaderCell>Hora</CustomHeaderCell>
            <CustomCell dataKey="Hora" />
          </Column>

          <Column style={colStyle} width={100}>
            <CustomHeaderCell>Data</CustomHeaderCell>
            <CustomCell>
              {(rowData) => formataDataSemHora(rowData.DataPedido)}
            </CustomCell>
          </Column>

          <Column style={colStyle} flexGrow={1}>
            <CustomHeaderCell>Cliente</CustomHeaderCell>
            <CustomCell dataKey="Nome" />
          </Column>

          <Column style={colStyle} width={110}>
            <CustomHeaderCell>Status</CustomHeaderCell>
            <CustomCell>
              {(rowData) => (
                <S.StatusBadge $status={rowData.Status}>
                  {rowData.Status}
                </S.StatusBadge>
              )}
            </CustomCell>
          </Column>

          <Column style={colStyle} width={100}>
            <CustomHeaderCell>Valor</CustomHeaderCell>
            <CustomCell>
              {(rowData) => moeda(parseFloat(rowData.ValorTotal))}
            </CustomCell>
          </Column>

          <Column style={colStyle} width={100}>
            <CustomHeaderCell>Tx. Entrega</CustomHeaderCell>
            <CustomCell>
              {(rowData) => moeda(parseFloat(rowData.TaxaEntrega))}
            </CustomCell>
          </Column>

          <Column style={colStyle} width={90}>
            <CustomHeaderCell>Tx. Adm</CustomHeaderCell>
            <CustomCell>
              {(rowData) => moeda(parseFloat(rowData.TaxaAdministracao))}
            </CustomCell>
          </Column>

          <Column style={colStyle} width={100}>
            <CustomHeaderCell>Total</CustomHeaderCell>
            <CustomCell>
              {(rowData) =>
                moeda(
                  parseFloat(rowData.ValorTotal) +
                    parseFloat(rowData.TaxaEntrega) +
                    parseFloat(rowData.TaxaAdministracao),
                )
              }
            </CustomCell>
          </Column>

          <Column style={colStyle} flexGrow={1}>
            <CustomHeaderCell>Pagamento</CustomHeaderCell>
            <CustomCell dataKey="FormaPagamento" />
          </Column>

          <Column style={colStyle} width={100}>
            <CustomHeaderCell>Tipo</CustomHeaderCell>
            <CustomCell>
              {(rowData) =>
                deliveryTypeLabels[rowData.TipoEntrega] || rowData.TipoEntrega
              }
            </CustomCell>
          </Column>

          <Column style={colStyle} width={140} fixed="right">
            <CustomHeaderCell>Opcoes</CustomHeaderCell>
            <CustomCell>
              {(rowData) => (
                <S.RowActions>
                  <S.RowBtn
                    onClick={() => abrirDados(rowData)}
                    title="Ver pedido"
                  >
                    <FaEye size={12} />
                    Pedido
                  </S.RowBtn>
                  {!config?.DisableOrdersActions && (
                    <S.RowBtn
                      onClick={() => imprimirPedido(rowData)}
                      title="Imprimir"
                    >
                      <FaPrint size={12} />
                    </S.RowBtn>
                  )}
                </S.RowActions>
              )}
            </CustomCell>
          </Column>
        </Table>
      </S.TableWrapper>

      {/* ---- Pagination ---- */}
      <S.PaginationBar>
        <Pagination
          prev
          next
          first
          last
          ellipsis
          boundaryLinks
          maxButtons={5}
          size="xs"
          layout={['-', 'limit', '|', 'pager']}
          total={qtdPedidos}
          limitOptions={[10, 30, 50, 100]}
          limit={limite}
          activePage={pag}
          onChangePage={setPag}
          onChangeLimit={handleChangeLimit}
        />
      </S.PaginationBar>

      {open && (
        <Modal
          open={open}
          onClose={() => setOpen(false)}
          pedido={pedidoSelecionado}
          refresh={atualizar}
          title={`Detalhes do pedido - ${pedidoSelecionado.Codigo}`}
        >
          <DetalhesPedido
            onClose={() => setOpen(false)}
            pedido={pedidoSelecionado}
            entregadores={entregadores}
            refresh={atualizar}
            formatarData={formataDataCompleta}
          />
        </Modal>
      )}
    </S.PageWrapper>
  );
}
